import {
  PrismaEnums,
  WithAuthRole,
  isAdminOnlyUser,
  isAirAdminRole,
  isAirLimitedAdminCoaches,
  isFullAirAdmin,
  isStudentRole,
} from '@airreading/common';

export const allRoles = Object.values(PrismaEnums.Role);
export const RolesLabelMap: Record<PrismaEnums.Role, string> = {
  [PrismaEnums.Role.student]: 'Student',
  [PrismaEnums.Role.parent]: 'Parent',
  [PrismaEnums.Role.coach]: 'Teacher',
  [PrismaEnums.Role.org_admin]: 'District Admin',
  [PrismaEnums.Role.air_admin]: 'Air Admin',
  [PrismaEnums.Role.air_limited_admin]: 'Air Limited Admin',
  [PrismaEnums.Role.air_limited_admin_coaches]: 'Air Limited Admin Teachers',
  [PrismaEnums.Role.school_admin]: 'School Admin',
  [PrismaEnums.Role.school_contact]: 'School Contact',
  [PrismaEnums.Role.edge_coach]: 'Edge Tutors',
};

export type AirAdminOrLimitedAdminRole =
  | typeof PrismaEnums.Role.air_admin
  | typeof PrismaEnums.Role.air_limited_admin
  | typeof PrismaEnums.Role.air_limited_admin_coaches;

/**
 * Active Air admin roles, in menu order. Also the roles eligible to be assigned
 * as a student-review reviewer (learning-issue create/edit and missed GLA edit).
 * The user typeahead drops soft-deleted users and roles, so an inactive account
 * that no longer holds one of these roles does not appear.
 */
export const studentReviewReviewerRoles: readonly AirAdminOrLimitedAdminRole[] = [
  PrismaEnums.Role.air_admin,
  PrismaEnums.Role.air_limited_admin,
  PrismaEnums.Role.air_limited_admin_coaches,
];

/** Air admin roles in menu order. */
const AIR_ADMIN_OR_LIMITED_ADMIN_ROLES = studentReviewReviewerRoles;

/**
 * Which Air admin role (`air_admin`, `air_limited_admin`, or
 * `air_limited_admin_coaches`) the user actually holds, or null if they hold
 * none. Returns the first in that order in the (invalid) case where several are held.
 * Unlike `isAirAdmin`, this distinguishes the roles. Use it whenever
 * escalation of roles or privileges is relevant.
 */
export function heldAirAdminOrLimitedAdminRole(user: WithAuthRole): AirAdminOrLimitedAdminRole | null {
  const userRoles = 'user_roles' in user ? user.user_roles : user.userRoles;
  return AIR_ADMIN_OR_LIMITED_ADMIN_ROLES.find((adminRole) => userRoles.some(({ role }) => role === adminRole)) ?? null;
}

/**
 * The Air admin roles `actingUser` may manage on `user`: the one role the user
 * holds (a revoke), or every Air admin role when they hold none (a promote
 * each). Empty for anyone who is not a full `air_admin`.
 */
export function manageableAirAdminRoles(user: WithAuthRole, actingUser: WithAuthRole): AirAdminOrLimitedAdminRole[] {
  if (!isFullAirAdmin(actingUser)) {
    return [];
  }

  const heldRole = heldAirAdminOrLimitedAdminRole(user);
  return heldRole ? [heldRole] : [...AIR_ADMIN_OR_LIMITED_ADMIN_ROLES];
}

/**
 * Whether `actingUser` may create any user (new accounts, not profiles or roles
 * added to an existing user). Only a full `air_admin` may: an
 * `air_limited_admin` sees PII masked, so they could not read back the account
 * they just created. For coach-only accounts see {@link canCreateCoachUsers}.
 * Enforced server-side by `assertCanCreateUser` in
 * `apps/server/src/auth/user-creation.ts`.
 */
export function canCreateUsers(actingUser: WithAuthRole): boolean {
  return isFullAirAdmin(actingUser);
}

/**
 * Whether `actingUser` may create coach users: accounts whose only roles are
 * `coach` / `edge_coach`, manually or by coach CSV / v1 import. A full
 * `air_admin` or an `air_limited_admin_coaches` may. Enforced server-side by
 * `assertCanCreateUser`.
 */
export function canCreateCoachUsers(actingUser: WithAuthRole): boolean {
  return isFullAirAdmin(actingUser) || isAirLimitedAdminCoaches(actingUser);
}

/**
 * Whether `actingUser` may purge a client's data. Only a full `air_admin` may:
 * purging is irreversible, and an `air_limited_admin` sees PII masked, so they
 * could not confirm what they were about to destroy. Enforced server-side in
 * `assertCanPurgeData`.
 */
export function canPurgeData(actingUser: WithAuthRole): boolean {
  return isFullAirAdmin(actingUser);
}

/**
 * Whether `actingUser` may impersonate anyone: a full `air_admin` or an
 * `air_limited_admin_coaches`. See {@link impersonationBlockReason} for which
 * targets each may pick.
 */
export function canImpersonate(actingUser: WithAuthRole): boolean {
  return isFullAirAdmin(actingUser) || isAirLimitedAdminCoaches(actingUser);
}

/**
 * The roles an impersonation target must hold one of for `actingUser` to pick
 * them, used to narrow the user search. Null when any role is allowed; empty
 * when `actingUser` may not impersonate anyone. Coarser than
 * {@link impersonationBlockReason}, which still decides each user, e.g. a coach
 * who is also an Air admin or student.
 */
export function impersonationTargetRoles(actingUser: WithAuthRole): PrismaEnums.Role[] | null {
  if (isFullAirAdmin(actingUser)) {
    return null;
  }
  if (isAirLimitedAdminCoaches(actingUser)) {
    return [
      PrismaEnums.Role.coach,
      PrismaEnums.Role.edge_coach,
      PrismaEnums.Role.school_admin,
      PrismaEnums.Role.org_admin,
    ];
  }
  return [];
}

/**
 * Why `actingUser` may not impersonate `target`, or null when they may. Mirrors
 * `assertCanImpersonate` in `apps/server/src/auth/impersonation-access.ts`,
 * evaluated from the most permissive role the actor holds:
 * - full `air_admin`: anyone except an admin-only user;
 * - `air_limited_admin_coaches`: only users who are neither students nor
 *   Air admins. School/org admins are external customers and may be impersonated;
 * - anyone else: no one.
 */
export function impersonationBlockReason(actingUser: WithAuthRole, target: WithAuthRole): string | null {
  if (isFullAirAdmin(actingUser)) {
    return isAdminOnlyUser(target) ? 'Cannot impersonate admin-only users' : null;
  }
  if (isAirLimitedAdminCoaches(actingUser)) {
    const targetRoles = 'user_roles' in target ? target.user_roles : target.userRoles;
    const isStudentOrAirAdmin = targetRoles.some((role) => isStudentRole(role) || isAirAdminRole(role));
    return isStudentOrAirAdmin ? 'Cannot impersonate students or Air admins' : null;
  }
  return 'Cannot impersonate users';
}

/**
 * Label for the menu item and dialog managing `role`, e.g. "Revoke Air Limited
 * Admin" or "Promote as Air Limited Admin"
 */
export function manageAirAdminLabel(user: WithAuthRole, role: AirAdminOrLimitedAdminRole): string {
  const isHeld = heldAirAdminOrLimitedAdminRole(user) === role;
  return `${isHeld ? 'Revoke' : 'Promote as'} ${RolesLabelMap[role]}`;
}
